/**
 * Edge-safe detector that doesn't use WebAssembly
 * This is used in Edge Runtime where dynamic code evaluation is not allowed
 */

import type { DetectionResult, DetectionInput } from '@kya-os/agentshield-shared';

// Known AI agent patterns
const AI_AGENT_PATTERNS = [
  { pattern: /chatgpt-user/i, type: 'chatgpt', name: 'ChatGPT' },
  { pattern: /claude-web/i, type: 'claude', name: 'Claude' },
  { pattern: /perplexitybot/i, type: 'perplexity', name: 'Perplexity' },
  { pattern: /perplexity-user/i, type: 'perplexity', name: 'Perplexity' },
  { pattern: /perplexity/i, type: 'perplexity', name: 'Perplexity' },
  { pattern: /bingbot/i, type: 'bing', name: 'Bing AI' },
  { pattern: /anthropic-ai/i, type: 'anthropic', name: 'Anthropic' },
];

/**
 * Edge-safe pattern detection without WASM
 */
export class EdgeSafeDetector {
  async analyze(input: DetectionInput): Promise<DetectionResult> {
    const reasons: string[] = [];
    let detectedAgent: { type: string; name: string } | undefined;
    let confidence = 0;

    const headers = input.headers || {};
    const normalizedHeaders: Record<string, string> = {};

    // Normalize header names to lowercase
    for (const [key, value] of Object.entries(headers)) {
      normalizedHeaders[key.toLowerCase()] = value;
    }

    // Check User-Agent patterns
    const userAgent = input.userAgent || normalizedHeaders['user-agent'] || '';
    if (userAgent) {
      for (const { pattern, type, name } of AI_AGENT_PATTERNS) {
        if (pattern.test(userAgent)) {
          confidence = 85; // Updated to 0-100 scale (was 0.85)
          reasons.push(`known_pattern:${type}`);
          detectedAgent = { type, name };
          break;
        }
      }
    }

    // Check for browser UA without browser headers (Perplexity pattern)
    const hasChrome = userAgent.toLowerCase().includes('chrome');
    const hasFirefox = userAgent.toLowerCase().includes('firefox');
    const hasSafari = userAgent.toLowerCase().includes('safari');
    const hasBrowserUA = hasChrome || hasFirefox || hasSafari;

    if (hasBrowserUA) {
      const hasSecChUa = !!normalizedHeaders['sec-ch-ua'];
      const hasSecFetch = !!normalizedHeaders['sec-fetch-site'];
      const hasAcceptLanguage = !!normalizedHeaders['accept-language'];
      const hasCookies = !!normalizedHeaders['cookie'];

      const missingHeaders = [];
      if (!hasSecChUa && hasChrome) missingHeaders.push('sec-ch-ua');
      if (!hasSecFetch) missingHeaders.push('sec-fetch');
      if (!hasAcceptLanguage) missingHeaders.push('accept-language');
      if (!hasCookies) missingHeaders.push('cookies');

      if (missingHeaders.length >= 2) {
        confidence = Math.max(confidence, 85); // Updated to 0-100 scale (was 0.85)
        reasons.push('browser_ua_missing_headers');
        if (!detectedAgent && hasChrome && !hasSecChUa) {
          detectedAgent = { type: 'perplexity', name: 'Perplexity' };
        }
      }
    }

    // Check for AI-specific headers
    const aiHeaders = ['openai-conversation-id', 'anthropic-client-id', 'x-goog-api-client'];

    const foundAiHeaders = aiHeaders.filter((h) => normalizedHeaders[h]);
    if (foundAiHeaders.length > 0) {
      confidence = Math.max(confidence, 60); // Updated to 0-100 scale (was 0.6)
      reasons.push(`ai_headers:${foundAiHeaders.length}`);
    }

    return {
      isAgent: confidence > 30, // Updated to 0-100 scale (was 0.3)
      confidence,
      detectionClass:
        confidence > 30 && detectedAgent // Updated to 0-100 scale
          ? { type: 'AiAgent' as const, agentType: detectedAgent.name }
          : confidence > 30 // Updated to 0-100 scale
            ? { type: 'Unknown' as const }
            : { type: 'Human' as const },
      signals: [], // Will be populated by enhanced detection engine in future tasks
      detectedAgent,
      reasons,
      verificationMethod: 'pattern',
      timestamp: new Date(),
      confidenceLevel: confidence >= 80 ? 'high' : confidence >= 50 ? 'medium' : 'low', // Updated to 0-100 scale
    };
  }
}
